import { useEffect, useMemo, useState } from 'react'
import { apiGet } from '../../Api_Functions'
import { useSmartTranslation } from '../useSmartTranslation'

// Import typedefs from another file
/**
 * @typedef {import('../../utilities/objectStructures/extendedcolumn/extendedColumn').ExtendedColumn} ExtendedColumn ExtendedColumn object
 * @typedef {import('../../utilities/objectStructures/extendedcolumn/extendedColumn').DataType} DataType Posible data types
 */

/**
 * Get smartTable column type by extended column dataType
 * @param {DataType} type
 */
function getExtendedColumnsType (type) {
  let tableType
  switch (type) {
    case 'DATE':
      tableType = 'dateTimePicker'
      break
    case 'BOOLEAN':
      tableType = 'boolean'
      break
    case 'LIST':
      tableType = 'smartSelect'
      break
    default:
      tableType = undefined
  }
  return tableType
}

/**
 * Recover the extended columns for the specified table
 * @param {'fabrication_order'|'order'|'part_reference'|'raw_material_reference'|'raw_material'|'feature'|'workflow_resource'|'job_reference'|'workflow'} tableName Table name of which extended columns will be retrieved
 */
const useGetExtendedColumns = (tableName) => {
  const [firstLoad, setFirstLoad] = useState(true)
  const [extendedColumns, setExtendedColumns] = useState()
  const { t_ } = useSmartTranslation()

  useEffect(() => {
    const posibleTables = ['fabrication_order', 'order', 'part_reference', 'raw_material_reference', 'raw_material', 'feature', 'workflow_resource', 'job_reference', 'workflow']
    const getExtendedColumns = async () => {
      // An unreadable definition list must not block its consumers (RN8): no columns, same screen.
      const response = await apiGet('extendedColumn/' + tableName).catch(() => [])
      setExtendedColumns(response)
    }
    if (firstLoad) {
      setFirstLoad(false)
      if (!posibleTables.includes(tableName)) {
        setExtendedColumns([])
        return
      }
      getExtendedColumns()
    }
  }, [firstLoad, tableName])

  return useMemo(() => {
    if (firstLoad || extendedColumns === undefined) {
      return undefined
    }

    const tableExtendedColumn = []
    extendedColumns?.forEach((extendedColumn, index) => {
      let column = {
        name: t_(extendedColumn.label),
        field: extendedColumn.field,
        type: getExtendedColumnsType(extendedColumn.dataType),
        mandatory: extendedColumn.mandatory
      }
      if (extendedColumn.dataType === 'LIST') {
        column = {
          ...column,
          isSimpleData: true,
          type: 'smartSelect',
          edit: {
            source: () => Promise.resolve(extendedColumn.options)
          },
          style: { minWidth: '10rem' }
        }
      }
      if (extendedColumn.dataType === 'NUMERIC') {
        column = { ...column, numeric: true }
      }
      if (extendedColumn.dataType === 'BOOLEAN') {
        column = { ...column, defaultNewValue: false }
      }
      tableExtendedColumn.push(column)
    })

    return tableExtendedColumn?.length !== 0 ? tableExtendedColumn : []
  }, [extendedColumns, firstLoad, t_])
}

export default useGetExtendedColumns
